import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Statistic, Progress, Typography, Space, Row, Col, Table, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import metaData from "../../../data/metaData";

//
import UserStatus from "../../../components/Table/UserStatus";
import ActionHeader from "../../../components/Table/ActionHeader";
import FilterDropdown from "../../../components/Table/FIlterDropdown";
import Avatar from "boring-avatars";

//
import useAuthStore from "../../../states/useAuth";
import useUserStore from "../../../states/useUser";
import useUser from "../../../hooks/useUser";
import Meta from "../../../components/Meta";

const { Title, Text } = Typography;

const User = () => {
  const history = useHistory();
  const location = useLocation();

  const { auth, user } = useAuthStore();
  const { resetUser } = useUserStore();
  const { getUsers } = useUser();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Segment
  const [activeSegment, setAtiveSegment] = useState("all");

  //Table
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [data, setData] = useState([]);
  const [filteredDataCount, setFilteredDataCount] = useState(data.length);

  const [isFetchingUsers, setIsFetchingUsers] = useState([]);

  const pageRef = useRef();

  const filteredData = useMemo(() => {
    let filtered = data;

    setFilteredDataCount(filtered.length);

    return filtered.sort((a, b) => {
      // Sort by status first (pending over approved), then by lastModify in descending order
      // if (a.status !== b.status) {
      //   return a.status === "pending" ? -1 : 1;
      // }
      return b.name - a.name;
    });
  }, [data]);

  const columns = [
    // {
    //   title: "RequestId",
    //   dataIndex: "id",
    //   key: "id",
    //   render: (text, record) => <Paragraph copyable={{ text: record?.id }}>{record?.id}</Paragraph>,
    //   sorter: (a, b) => (a?.id).localeCompare(b?.id),
    // },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record) => (
        <Meta
          image={
            record?.logo ? (
              <img
                style={{ width: 40, height: 40, objectFit: "cover" }}
                src={metaData.blobPreURL + "clientUpload-" + record.logo + ".jpeg" + metaData.blobPostURL}
              />
            ) : (
              <Avatar size={30} name={text} />
            )
          }
          title={text}
          maxWidth={160}
        />
      ),
      sorter: (a, b) => (a?.name).localeCompare(b?.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text, record) => <Text>{text}</Text>,
      sorter: (a, b) => (a?.email).localeCompare(b?.email),
    },
    {
      title: "Status",
      dataIndex: "isDisable",
      key: "isDisable",
      render: (text) => <UserStatus status={text} />,
      sorter: (a, b) => (a?.isDisable).localeCompare(b?.isDisable),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (text, record) => (
        <Space
          style={{
            fontSize: "1rem",
          }}
        >
          <a
            onClick={() => {
              history.push("/merchantUser/user/" + record?.id);
            }}
          >
            detail
          </a>
        </Space>
      ),
    },
  ];

  // const generateData = (num) => {
  //   let users = [];
  //   const statuses = [true, false];

  //   for (let i = 1; i <= num; i++) {
  //     const user = {
  //       key: i,
  //       id: c.generateId(4, 6),
  //       name: `Name ${i}`,
  //       email: `${i}@gmail.com`,
  //       isDisable: statuses[Math.floor(Math.random() * statuses.length)], // Randomize status
  //     };
  //     users.push(user);
  //   }
  //   console.log(users);
  //   return users;
  // };

  // const rowSelection = {
  //   selectedRowKeys,
  //   onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  // };

  const handleActionClick = (key) => {
    log.debug(key);
  };

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getUsersData = async () => {
    if (!user?.companyId) {
      return;
    }
    
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    const queryParams = c.parseQueryToJSON(queryURL);
    const { pagination } = queryParams;
    log.debug(pagination);

    let result;
    try {
      setIsFetchingUsers(true);
      result = await getUsers(queryURL);

      //add key to disable antd table warning
      setData(
        result.map((e, i) => {
          return { ...e, key: i };
        })
      );
    } catch (error) {
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingUsers(false);
      log.debug(result);
    }
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetUser(); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getUsersData();
  }, [location.search]);

  return (
    <div style={{ margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>User & Permission</Title>
        <div>
          <Button
            size="large"
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginRight: "8px" }}
            onClick={() => {
              history.push("/merchantUser/user/new");
            }}
          >
            Add User
          </Button>
        </div>
      </div>

      <Row justify="space-between" style={{ width: "100%", marginBottom: "2rem" }}></Row>

      {/* Filter Group */}
      {/* {activeSegment === "all" ? (
        <Card bordered style={{ marginBottom: "20px", background: "var(--ion-color-step-50)" }}>
          <Row>
            <FilterDropdown
              label="Status"
              defaultValue="all"
              options={[
                { value: "all", label: "All Status" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
            />
          </Row>
        </Card>
      ) : null} */}

      <ActionHeader
        pagination={pagination}
        pageSize={20}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        count={filteredDataCount}
        onActionClick={handleActionClick}
      />

      <Table
        className="white-table-header"
        // rowSelection={rowSelection} disabled multiple selection
        rowSelection={undefined}
        sticky
        loading={isFetchingUsers}
        columns={columns}
        dataSource={filteredData}
        pagination={{
          current: pagination,
          pageSize: 20,
          position: ["none", "bottomCenter"],
          showSizeChanger: false,
        }}
        onChange={handleTableChange} // This handles the next/prev page
      />
    </div>
  );
};

export default User;
